import React, { useEffect, useState } from "react";
import { FiltersProps } from "./DirectoryFilters";
import { locationService } from "@/services/locationListingService";
import { Location, LocationResponse } from "@/types/location";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Star, Phone, Image as ImageIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import GuestQuickActions from "./GuestQuickActions";

interface DirectoryTableViewProps {
  filters: FiltersProps;
  onResultsChange?: (hasResults: boolean, count?: number) => void;
}

const DirectoryTableView: React.FC<DirectoryTableViewProps> = ({ 
  filters,
  onResultsChange 
}) => {
  const [listings, setListings] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [favoriteIds, setFavoriteIds] = useState<number[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('favourite_locations') || '[]');
    } catch {
      return [];
    }
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0
  });

  useEffect(() => {
    setLoading(true);
    setError(null);
    
    // If showing favorites only, filter from localStorage
    if (filters.showFavoritesOnly) {
      const favoriteIds = JSON.parse(localStorage.getItem('favourite_locations') || '[]');
      if (favoriteIds.length === 0) {
        setListings([]);
        setPagination({
          page: 1,
          per_page: 20,
          total: 0,
          total_pages: 0
        });
        setLoading(false);
        if (onResultsChange) onResultsChange(false, 0);
        return;
      }
      
      // For favorites, we'll need to fetch each location individually or modify the API call
      // For now, we'll show a message that favorites filtering requires the full list
      setError("Favorites filtering requires loading all locations first. Please disable the favorites filter to see your favorites.");
      setLoading(false);
      if (onResultsChange) onResultsChange(false, 0);
      return;
    }
    
    locationService.getLocations({
      country: filters.selectedCountry,
      county: filters.selectedCounties?.join(",") || undefined,
      type: filters.type !== 'all' ? filters.type : undefined,
      search: filters.search || undefined,
      page: !filters.showAll ? pagination.page : undefined,
      per_page: !filters.showAll ? pagination.per_page : undefined,
      serviceTypes: filters.serviceTypes,
      placeTypes: filters.placeTypes,
      experiences: filters.experiences,
      amenities: filters.amenities,
      showAll: filters.showAll,
      location: filters.location
    })
      .then((response: LocationResponse) => {
        let filteredListings = response.data;
        
        // Apply favorites filter if enabled
        if (filters.showFavoritesOnly) {
          filteredListings = response.data.filter(location => 
            favoriteIds.includes(Number(location.id))
          );
        }
        
        setListings(filteredListings);
        setPagination({
          page: response.meta.current_page,
          per_page: response.meta.per_page,
          total: filteredListings.length,
          total_pages: Math.ceil(filteredListings.length / response.meta.per_page)
        });
        setLoading(false);
        if (onResultsChange) onResultsChange(filteredListings.length > 0, filteredListings.length);
      })
      .catch((error) => {
        console.error("Error fetching locations:", error);
        setError("Failed to load locations");
        setLoading(false);
        setListings([]);
        if (onResultsChange) onResultsChange(false, 0);
      });
  }, [filters, pagination.page, pagination.per_page, onResultsChange, favoriteIds]);

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getPlanBadge = (plan: string) => {
    switch(plan) {
      case 'premium':
        return <Badge className="bg-sauna-orange text-white">Premium</Badge>;
      case 'basic':
        return <Badge className="bg-sauna-blue text-white">Basic</Badge>;
      default:
        return <Badge className="text-gray-600 border-gray-400">Free</Badge>;
    }
  };

  const toggleFavorite = (locationId: number) => {
    setFavoriteIds(prev => {
      const newFavorites = prev.includes(locationId) 
        ? prev.filter(id => id !== locationId)
        : [...prev, locationId];
      localStorage.setItem('favourite_locations', JSON.stringify(newFavorites));
      return newFavorites;
    });
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-12">
      <LoadingSpinner size="lg" variant="default" />
      <p className="mt-4 text-sauna-navy font-medium">Loading listings...</p>
    </div>
  );
  if (error) return <div className="text-red-500">{error}</div>;
  if (listings.length === 0) return <div>No listings found.</div>;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Operator</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
              <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Favorites</th>
              <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {listings.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <img
                      src={item.image || '/assets/sauna-placeholder.jpg'}
                      alt={item.name}
                      className="w-10 h-10 rounded object-cover bg-gray-100"
                    />
                    <span className="font-medium text-sauna-navy">{item.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {item.operator?.name ? (
                    item.operator.website ? (
                      <a href={item.operator.website} target="_blank" rel="noopener noreferrer" className="hover:underline">
                        {item.operator.name}
                      </a>
                    ) : (
                      item.operator.name
                    )
                  ) : '-'}
                </td>
                <td className="px-4 py-3 text-gray-600">{item.location}</td>
                <td className="px-4 py-3 text-gray-600 capitalize">{item.type}</td>
                <td className="px-4 py-3">{getPlanBadge(item.plan || 'free')}</td>
                <td className="px-4 py-3">{item.rating ? `⭐ ${item.rating}` : '-'}</td>
                <td className="px-4 py-3">{item.price || '-'}</td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => toggleFavorite(Number(item.id))}
                    className="text-sauna-orange hover:text-sauna-orange/80 transition-colors"
                  >
                    <Star 
                      className={`h-5 w-5 ${favoriteIds.includes(Number(item.id)) ? 'fill-current' : 'fill-none'}`} 
                    />
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <GuestQuickActions location={item} className="justify-end" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <Button
              className="bg-white hover:bg-gray-50"
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
            >
              Previous
            </Button>
            <Button
              className="bg-white hover:bg-gray-50"
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.total_pages}
            >
              Next
            </Button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{(pagination.page - 1) * pagination.per_page + 1}</span> to{" "}
                <span className="font-medium">
                  {Math.min(pagination.page * pagination.per_page, pagination.total)}
                </span>{" "}
                of <span className="font-medium">{pagination.total}</span> results
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                className="bg-white hover:bg-gray-50"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: pagination.total_pages }, (_, i) => i + 1)
                  .filter(page => {
                    // Show first page, last page, current page, and pages around current page
                    return (
                      page === 1 ||
                      page === pagination.total_pages ||
                      Math.abs(page - pagination.page) <= 1
                    );
                  })
                  .map((page, index, array) => {
                    // Add ellipsis between non-consecutive pages
                    if (index > 0 && page - array[index - 1] > 1) {
                      return (
                        <React.Fragment key={`ellipsis-${page}`}>
                          <span className="px-2">...</span>
                          <Button
                            className={page === pagination.page ? "bg-sauna-orange text-white hover:bg-sauna-orange/90" : "bg-white hover:bg-gray-50"}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </Button>
                        </React.Fragment>
                      );
                    }
                    return (
                      <Button
                        key={page}
                        className={page === pagination.page ? "bg-sauna-orange text-white hover:bg-sauna-orange/90" : "bg-white hover:bg-gray-50"}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </Button>
                    );
                  })}
              </div>
              <Button
                className="bg-white hover:bg-gray-50"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.total_pages}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DirectoryTableView;
